'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { WizardStepIndicator } from './wizard-step-indicator';
import { StepAccount } from './step-account';
import { StepCharityInfo } from './step-charity-info';
import { StepGiftAid } from './step-gift-aid';
import { StepReview } from './step-review';
import { StepCheckEmail } from './step-check-email';

// Master form schema — used for step 1+ submission. Account fields
// are intentionally lax here (allow empty) because the wizard re-mounts
// in a fresh tab after the verification-link click, with no client
// state for them; they live in the pending DB row at that point.
//
// Strict account-field validation happens via `accountStepSchema`
// only on step 0's handleNext, before posting to /api/register/start.
const formSchema = z.object({
  adminName: z.string().optional(),
  adminEmail: z.string().optional(),
  adminPassword: z.string().optional(),
  confirmPassword: z.string().optional(),
  charityName: z.string().min(2, 'Charity name must be at least 2 characters'),
  charityNumber: z.string().optional(),
  subdomain: z
    .string()
    .min(3, 'Subdomain must be at least 3 characters')
    .max(30, 'Subdomain must be at most 30 characters')
    .regex(/^[a-z0-9]+$/, 'Subdomain must be lowercase alphanumeric only')
    .transform((val) => val.toLowerCase()),
  giftAidStartDate: z.enum(['current', 'last_year', 'earlier', 'not_claiming']),
  // GASDS scheme distinction kept at signup because Community Buildings
  // mode unlocks per-building cap tracking. The "Do you collect small
  // cash donations at all?" question was dropped — every charity can
  // record GASDS donations from the dashboard if they want, and the
  // signup question was only gating UI that's now unconditional.
  operatesCommunityBuildings: z.boolean(),
});

const accountStepSchema = z
  .object({
    adminName: z.string().optional(),
    adminEmail: z.string().email('Please enter a valid email address'),
    adminPassword: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.adminPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type SignupFormData = z.infer<typeof formSchema>;

// localStorage key for the email captured on step 0. Persisted across
// the verify-link redirect so the Review screen in the new tab can
// display "Email: foo@bar.org" without an extra round-trip.
const EMAIL_STORAGE_KEY = 'tk_signup_email';

const STEPS = [
  { label: 'Account' },
  { label: 'Charity' },
  { label: 'Gift Aid' },
  { label: 'Review' },
];

const STEP_FIELDS: (keyof SignupFormData)[][] = [
  ['adminEmail', 'adminPassword', 'confirmPassword'],
  ['charityName', 'subdomain'],
  ['giftAidStartDate', 'operatesCommunityBuildings'],
  [],
];

const fadeInUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.25 },
};

// "phase" is what's currently rendered — separate from `currentStep`,
// which only advances among the four real form pages. The check-email
// interstitial is its own phase so it can opt out of the step indicator.
type Phase = 'form' | 'pending';

export function SignupWizard() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // If the URL carries a verified=1&token=... pair (the user just clicked
  // the link in their email), start at the Charity step with the token in
  // hand. Read once via lazy initialisers — same value on server and client.
  const verifiedToken = (() => {
    const token = searchParams.get('token');
    return token && searchParams.get('verified') === '1' ? token : null;
  })();
  const [phase, setPhase] = useState<Phase>('form');
  const [currentStep, setCurrentStep] = useState(verifiedToken ? 1 : 0);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous double-submit guard: `disabled={isLoading}` stops a
  // user-driven double-click on the next render, but two events fired
  // in the same tick (e.g. form `submit` + button `click`) can both
  // pass through before that re-render. Once we begin submitting we
  // never want a second invocation — the pending row gets deleted on
  // the first 200 and a duplicate would 403 with a confusing error.
  const submittingRef = useRef(false);
  // Continuation token from the email-link click. Present on the URL
  // when the wizard re-opens in a fresh tab; carried forward to the
  // final POST so the server can pair the form data with the verified
  // pending registration.
  const [pendingToken] = useState<string | null>(verifiedToken);

  const form = useForm<SignupFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      adminName: '',
      adminEmail: '',
      adminPassword: '',
      confirmPassword: '',
      charityName: '',
      charityNumber: '',
      subdomain: '',
      giftAidStartDate: 'not_claiming',
      operatesCommunityBuildings: false,
    },
    mode: 'onTouched',
  });

  // Arriving via the verification link: hydrate the email from localStorage
  // so the Review screen can show it. Best-effort: if localStorage has been
  // cleared (e.g. user clicked the link on a different device), Review just
  // omits the email row — finalise still works because the email lives
  // server-side in the pending row. The token stays in state until the
  // final POST; after that we strip it from the URL.
  useEffect(() => {
    if (!verifiedToken) return;
    try {
      const saved = window.localStorage.getItem(EMAIL_STORAGE_KEY);
      if (saved) form.setValue('adminEmail', saved);
    } catch {
      /* localStorage unavailable — ignore */
    }
  }, [verifiedToken, form]);

  const handleNext = async () => {
    // Step 0 has its own strict account schema; later steps validate
    // against the (lax) master form schema.
    if (currentStep === 0) {
      const result = accountStepSchema.safeParse({
        adminName: form.getValues('adminName'),
        adminEmail: form.getValues('adminEmail'),
        adminPassword: form.getValues('adminPassword'),
        confirmPassword: form.getValues('confirmPassword'),
      });
      if (!result.success) {
        // Surface zod errors on the form so the inline messages render.
        for (const issue of result.error.issues) {
          const path = issue.path[0] as keyof SignupFormData;
          if (path) form.setError(path, { message: issue.message });
        }
        return;
      }
      await submitStart();
      return;
    }

    const valid = await form.trigger(STEP_FIELDS[currentStep]);
    if (!valid) return;
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  async function submitStart() {
    setIsLoading(true);
    setError(null);
    try {
      const { adminEmail, adminPassword, adminName } = form.getValues();
      const response = await fetch('/api/register/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: adminEmail,
          password: adminPassword,
          name: adminName || undefined,
        }),
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        setError(result.error || 'Failed to start signup');
        return;
      }
      try {
        if (adminEmail) window.localStorage.setItem(EMAIL_STORAGE_KEY, adminEmail);
      } catch {
        /* localStorage unavailable — Review will simply omit email */
      }
      setPhase('pending');
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }

  const onSubmit = async (data: SignupFormData) => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    if (!pendingToken) {
      // Should be unreachable: the wizard only renders steps 1–3 once
      // a token is present, and Review's submit is hidden otherwise.
      setError('Missing verification token. Restart signup.');
      submittingRef.current = false;
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/register/finalise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: pendingToken,
          charityName: data.charityName,
          charityNumber: data.charityNumber || undefined,
          subdomain: data.subdomain,
          giftAidStartDate: data.giftAidStartDate,
          operatesCommunityBuildings: data.operatesCommunityBuildings,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        setError(result.error || 'Failed to create account');
        // Allow retry on recoverable errors (e.g. subdomain clash).
        submittingRef.current = false;
        return;
      }
      try {
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      router.push('/login?message=Account created. Please sign in to continue.');
      // Don't reset submittingRef here — we're navigating away and
      // any second submit before the route change should be a no-op.
    } catch {
      setError('An unexpected error occurred. Please try again.');
      submittingRef.current = false;
    } finally {
      setIsLoading(false);
    }
  };

  // Pending interstitial: own layout, no form footer, no step nav.
  if (phase === 'pending') {
    return (
      <div className="w-full max-w-xl mx-auto">
        <StepCheckEmail
          email={form.getValues('adminEmail') ?? ''}
          onResend={submitStart}
          onBack={() => {
            setPhase('form');
            setError(null);
          }}
        />
      </div>
    );
  }

  // Once a token is present, the user can't go back to step 0 — that
  // form data has already been committed to the pending row. Hide the
  // back button on step 1 in that case.
  const canGoBack = currentStep > 0 && !pendingToken;

  return (
    <div className="w-full max-w-xl mx-auto">
      <WizardStepIndicator steps={STEPS} currentStep={currentStep} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <AnimatePresence mode="wait">
            <motion.div key={currentStep} {...fadeInUp}>
              {currentStep === 0 && <StepAccount form={form} isLoading={isLoading} />}
              {currentStep === 1 && <StepCharityInfo form={form} isLoading={isLoading} />}
              {currentStep === 2 && <StepGiftAid form={form} />}
              {currentStep === 3 && <StepReview form={form} />}
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between pt-4">
            {canGoBack ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isLoading}
              >
                Back
              </Button>
            ) : (
              <span />
            )}

            {currentStep < STEPS.length - 1 ? (
              <Button type="button" onClick={handleNext} disabled={isLoading}>
                {currentStep === 0 && isLoading
                  ? 'Sending verification email…'
                  : 'Next'}
              </Button>
            ) : (
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Creating Account…' : 'Create Account'}
              </Button>
            )}
          </div>

          <p className="text-sm text-center text-gray-600">
            Already have an account?{' '}
            <a href="/login" className="text-trust-blue hover:underline">
              Login
            </a>
          </p>
        </form>
      </Form>
    </div>
  );
}
